/**
 * Trainer side of live tests: the test builder, the results page, and the "Tests" tab on a batch.
 * Trainers set and run their own tests; the admin only oversees.
 */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDateTime, fromIstInput, toIstInput } from '../../core/format.js';
import { navigate } from '../../core/router.js';
import { currentView, every, reload, update } from '../../app/shell.js';
import { backLink, banner, empty, ic, stat, table, tag, who } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { toast } from '../../ui/toast.js';
import { BLANK_QUESTION, REASON, cleanQuestions, parseQuestions } from './lib.js';

/* ---------- the batch's Tests tab (used by professional/coaching.js) ---------- */

export function testState(t, now = Date.now()) {
  if (!t.published) return tag('Draft', 'n');
  if (now < Date.parse(t.opens_at)) return tag('Scheduled', 'wn');
  if (now <= Date.parse(t.closes_at)) return tag('Open now', 'ok');
  return tag('Closed', 'n');
}

export function testsTab(d) {
  return html`<div class="row between mb"><p class="mu sm" style="margin:0">Timed tests for this batch. Students are watched for tab switching and the test submits itself if they leave too often.</p>
      <a class="btn" href="/professional/tests/new?batch=${d.batch.id}">${ic('plus')} New test</a></div>
    ${d.tests.length
      ? table(['Test', 'Opens', 'Length', 'Status', 'Submitted', 'Flagged', ''], d.tests.map((t) => [
        t.title, fmtDateTime(t.opens_at), `${t.duration_min} min, ${t.question_count} questions`, testState(t),
        `${t.submitted} of ${d.students.length}`, t.flagged ? tag(`${t.flagged} flagged`, 'wn') : '-',
        html`<div class="row nw"><a class="btn ghost sm" href="/professional/tests/${t.id}">${t.published ? 'Results' : 'Open'}</a>
          ${t.published ? '' : html`<button type="button" class="btn sm" data-act="test.publish" data-id="${t.id}" data-title="${t.title}">Publish</button>`}</div>`,
      ]))
      : empty('clip', 'No tests yet', 'Set a timed test to check what students have learned.')}`;
}

/* ---------- builder ---------- */

const questionBlock = (q, i) => html`<div class="card mb tq" data-i="${i}">
  <div class="row between"><b>Question ${i + 1}</b><button type="button" class="btn ghost sm" data-act="tq.remove" data-i="${i}">Remove</button></div>
  <div class="field mt-s"><label>Question</label><textarea data-q rows="2" maxlength="400" placeholder="Type the question" aria-label="Question ${i + 1}">${q.q}</textarea></div>
  <p class="mu xs" style="margin:6px 0">Type the options and tick the correct one. Leave an option empty if you need fewer than four.</p>
  ${q.options.map((o, j) => html`<div class="row" style="margin-bottom:6px"><input type="radio" name="ans-${i}" value="${j}" ${q.answer === j ? 'checked' : ''} aria-label="Option ${'ABCDEF'[j]} is correct">
    <input data-o type="text" maxlength="200" value="${o}" placeholder="Option ${'ABCDEF'[j]}" aria-label="Question ${i + 1} option ${'ABCDEF'[j]}" style="flex:1"></div>`)}</div>`;

function readQuestions() {
  return Array.from(document.querySelectorAll('#test-form .tq')).map((div) => {
    const picked = div.querySelector('input[type=radio]:checked');
    return {
      q: div.querySelector('[data-q]').value,
      options: Array.from(div.querySelectorAll('[data-o]')).map((i) => i.value),
      answer: picked ? Number(picked.value) : null,
    };
  });
}

const soon = (minutes) => toIstInput(new Date(Date.now() + minutes * 60000).toISOString());

function builder(d, ui, editing) {
  if (ui.qs == null) ui.qs = editing ? d.test.questions.map((q) => ({ q: q.q, options: [...q.options, '', '', '', ''].slice(0, Math.max(4, q.options.length)), answer: q.answer })) : [BLANK_QUESTION(), BLANK_QUESTION(), BLANK_QUESTION()];
  const t = d.test || {};
  const frozen = editing && d.test.started > 0;
  const batchName = d.batch ? `${d.batch.course.title}, ${d.batch.name}` : '';
  return html`${backLink(`/professional/batches/${ui.batchId}?tab=tests`, 'Batch')}
    <div class="page-h"><div><h2 style="margin:0 0 4px">${editing ? 'Edit test' : 'New test'}</h2><p>${batchName}</p></div></div>
    ${frozen ? banner(`${d.test.started} student${d.test.started === 1 ? ' has' : 's have'} already started, so the questions and rules are locked. You can still change the title, instructions and closing time.`, 'wn', 'info') : ''}
    <form id="test-form" novalidate class="mb">
      <div class="card mb"><h3 style="margin-top:0">Details</h3>
        ${field('lt-title', 'Test name', { name: 'title', minlen: 2, maxlength: 160, ph: 'Week 2: Test design techniques', val: t.title || '' })}
        ${field('lt-ins', 'Instructions for students (optional)', { name: 'instructions', rows: 3, req: false, max: 3000, val: t.instructions || '', ph: 'Open notes are not allowed. Stay on this page.' })}
        <div class="fgrid">
          ${field('lt-open', 'Opens (India time)', { name: 'opens_at', type: 'datetime-local', val: t.opens_at ? toIstInput(t.opens_at) : soon(30), disabled: frozen })}
          ${field('lt-close', 'Students can start until (India time)', { name: 'closes_at', type: 'datetime-local', val: t.closes_at ? toIstInput(t.closes_at) : soon(150) })}
        </div>
        <div class="fgrid">
          ${field('lt-dur', 'Minutes each student gets', { name: 'duration_min', type: 'number', min: 5, max: 240, val: t.duration_min || 30, disabled: frozen })}
          ${field('lt-viol', 'Tab switches allowed before it submits', { name: 'max_violations', type: 'number', min: 1, max: 10, val: t.max_violations || 3, disabled: frozen, help: '3 means the third switch ends the test.' })}
          ${field('lt-pass', 'Pass mark (%)', { name: 'pass_percent', type: 'number', min: 1, max: 100, val: t.pass_percent || 50, disabled: frozen })}
        </div>
        <label class="opt" style="max-width:420px"><input type="checkbox" name="shuffle" ${t.shuffle === false ? '' : 'checked'} ${frozen ? 'disabled' : ''}> Shuffle questions and options for each student</label>
      </div>
      ${frozen ? '' : html`<h3>Questions (${ui.qs.length})</h3>
        <div id="tq-list">${ui.qs.map(questionBlock)}</div>
        <div class="row mb"><button type="button" class="btn ghost" data-act="tq.add">${ic('plus')} Add a question</button></div>
        <details class="card mb"><summary><b>Paste many questions at once</b></summary>
          <p class="mu sm">Separate questions with a blank line. Mark the right option with an Answer line:</p>
          <pre class="sm" style="white-space:pre-wrap">Q: What is a bug?\nA) A defect in the software\nB) A feature\nC) A meeting\nAnswer: A</pre>
          <textarea id="tq-bulk" rows="8" style="width:100%" aria-label="Paste questions"></textarea>
          ${ui.bulkErrors?.length ? banner(html`${ui.bulkErrors.map((e) => html`<div>${e}</div>`)}`, 'wn', 'alert') : ''}
          <div class="mt-s"><button type="button" class="btn sm" data-act="tq.bulk">Add these questions</button></div></details>`}
      <div class="row"><button type="button" class="btn ghost" data-act="test.save" data-publish="false">Save as draft</button>
        <button type="button" class="btn" data-act="test.save" data-publish="true">${ic('check')} Save and send to students</button></div>
      <p class="note mt-s">Drafts are only visible to you. Students get a notification when you send the test.</p>
    </form>`;
}

const newTest = {
  path: '/professional/tests/new',
  roles: ['professional'],
  nav: 'coaching',
  title: 'New test',
  ui: (ctx) => ({ batchId: Number(ctx.query.get('batch')) || 0, qs: null, bulkErrors: [] }),
  async load(ctx) {
    const { batches } = await api.get('/trainer/batches', { signal: ctx.signal });
    const batch = batches.find((b) => b.id === (Number(ctx.query.get('batch')) || 0));
    if (!batch) throw Object.assign(new Error('Open a batch you teach and choose "New test" there.'), { status: 404 });
    return { batch, test: null };
  },
  view: (d, ui) => builder(d, ui, false),
};

const editTest = {
  path: '/professional/tests/:id/edit',
  roles: ['professional'],
  nav: 'coaching',
  title: 'Edit test',
  ui: () => ({ batchId: 0, qs: null, bulkErrors: [] }),
  async load(ctx) {
    const { test } = await api.get(`/trainer/tests/${Number(ctx.params.id)}`, { signal: ctx.signal });
    const { batches } = await api.get('/trainer/batches', { signal: ctx.signal });
    return { test, batch: batches.find((b) => b.id === test.batch_id) };
  },
  view(d, ui) {
    ui.batchId = d.test.batch_id;
    return builder(d, ui, true);
  },
};

function syncQuestions() {
  const view = currentView();
  if (document.getElementById('test-form')) view.ui.qs = readQuestions();
}

async function saveTest(publish) {
  const view = currentView();
  const editing = Boolean(view.data.test);
  const form = document.getElementById('test-form');
  const v = Object.fromEntries(new FormData(form).entries());
  const frozen = editing && view.data.test.started > 0;
  const opens = frozen ? view.data.test.opens_at : fromIstInput(v.opens_at);
  const closes = fromIstInput(v.closes_at);
  if (!v.title || v.title.trim().length < 2) throw new Error('Give the test a name.');
  if (!opens || !closes) throw new Error('Choose when the test opens and when students can start until.');
  if (Date.parse(closes) <= Date.parse(opens) + 60000) throw new Error('"Start until" must be after the opening time.');
  if (publish && Date.parse(closes) <= Date.now()) throw new Error('The closing time has already passed.');
  let questions = view.data.test?.questions;
  if (!frozen) {
    const cleaned = cleanQuestions(readQuestions());
    if (cleaned.error) throw new Error(cleaned.error);
    questions = cleaned.questions;
  }
  const body = {
    batch_id: view.ui.batchId, title: v.title.trim(), instructions: (v.instructions || '').trim(),
    duration_min: Number(frozen ? view.data.test.duration_min : v.duration_min) || 30,
    max_violations: Number(frozen ? view.data.test.max_violations : v.max_violations) || 3,
    pass_percent: Number(frozen ? view.data.test.pass_percent : v.pass_percent) || 50,
    shuffle: frozen ? view.data.test.shuffle : v.shuffle === 'on',
    opens_at: opens, closes_at: closes, questions, publish,
  };
  const { test } = editing ? await api.put(`/trainer/tests/${view.data.test.id}`, { body }) : await api.post('/trainer/tests', { body });
  toast(publish ? 'Test sent. Students are notified.' : 'Draft saved', 'check');
  navigate(`/professional/tests/${test.id}`);
}

register({
  'tq.add': () => { syncQuestions(); update((ui) => { ui.qs.push(BLANK_QUESTION()); }); },
  'tq.remove': async (el) => {
    syncQuestions();
    update((ui) => { ui.qs.splice(Number(el.dataset.i), 1); if (!ui.qs.length) ui.qs.push(BLANK_QUESTION()); });
  },
  'tq.bulk': () => {
    syncQuestions();
    const { questions, errors } = parseQuestions(document.getElementById('tq-bulk').value);
    update((ui) => {
      ui.bulkErrors = errors;
      if (questions.length) {
        ui.qs = [...ui.qs.filter((q) => q.q.trim() || q.options.some((o) => o.trim())), ...questions.map((q) => ({ q: q.q, options: [...q.options, '', '', '', ''].slice(0, Math.max(4, q.options.length)), answer: q.answer }))];
        if (!errors.length) toast(`${questions.length} question${questions.length === 1 ? '' : 's'} added`, 'check');
      }
    });
  },
  'test.save': async (el) => {
    try {
      await saveTest(el.dataset.publish === 'true');
    } catch (error) {
      toast(error.message, 'alert');
    }
  },
  'test.publish': async (el) => {
    if (!(await confirmBox({ title: `Send "${el.dataset.title}" to students?`, text: 'Students in the batch are notified. You can still move the closing time later.', label: 'Send test' }))) return;
    await api.post(`/trainer/tests/${el.dataset.id}/publish`);
    toast('Test sent. Students are notified.', 'check');
    await reload();
  },
});

/* ---------- results ---------- */

const KIND = { tab_hidden: 'Switched tab', window_blur: 'Left the window', fullscreen_exit: 'Left full screen', reload: 'Reloaded or came back', copy_paste: 'Copy or paste' };

const results = {
  path: '/professional/tests/:id',
  roles: ['professional'],
  nav: 'coaching',
  title: (d) => (d ? d.test.title : 'Test'),
  load: (ctx) => api.get(`/trainer/tests/${Number(ctx.params.id)}/results`, { signal: ctx.signal }),
  enter() {
    every(20000, () => {
      const t = currentView()?.data?.test;
      if (t && Date.now() <= Date.parse(t.closes_at) + t.duration_min * 60000) return reload();
      return undefined;
    });
  },
  view(d) {
    const t = d.test;
    const s = d.summary;
    return html`${backLink(`/professional/batches/${t.batch_id}?tab=tests`, 'Batch')}
      <div class="page-h"><div><h2 style="margin:0 0 4px">${t.title}</h2><p>Opens ${fmtDateTime(t.opens_at)}. Students can start until ${fmtDateTime(t.closes_at)}. ${t.duration_min} minutes each, ${t.question_count} questions, ends after ${t.max_violations} tab switches.</p></div>
        <div class="row">${testState(t)}<a class="btn ghost sm" href="/professional/tests/${t.id}/edit">Edit</a></div></div>
      ${t.published ? '' : banner('This is a draft. Students cannot see it yet.', 'wn', 'info')}
      <div class="grid stats">${stat(s.submitted, 'Finished', `of ${s.students} students`)}${stat(s.average_percent == null ? '-' : `${s.average_percent}%`, 'Average score')}${stat(s.flagged, 'Flagged', 'switched tabs or left')}</div>
      <div class="mt">${table(['Student', 'Status', 'Score', 'Tab switches', 'How it ended', 'Time taken', ''], d.students.map((r) => [
        who(r.name),
        r.state === 'submitted' ? tag('Finished', 'ok') : r.state === 'in_progress' ? tag('Sitting now', 'wn') : tag('Not started', 'n'),
        r.state === 'submitted' ? html`<b>${r.percent}%</b> ${r.passed ? tag('Pass', 'ok') : tag('Below pass mark', 'bad')} <span class="mu xs">${r.score}/${r.total}</span>` : '-',
        r.violations ? tag(String(r.violations), r.violations >= t.max_violations ? 'bad' : 'wn') : (r.state === 'not_started' ? '-' : '0'),
        r.end_reason ? REASON[r.end_reason] : '-',
        r.minutes == null ? '-' : `${r.minutes} min`,
        r.state === 'not_started' ? '' : html`<div class="row nw"><button type="button" class="btn ghost sm" data-act="test.log" data-id="${r.student_id}">Activity</button>
          <button type="button" class="btn ghost sm" data-act="test.reset" data-id="${r.student_id}" data-name="${r.name}">Allow retake</button></div>`,
      ]), { empty: 'No students in this batch yet.' })}</div>
      <p class="note mt-s">A switch is recorded when a student changes tab, minimises the window or leaves full screen. Flagged does not prove cheating (a notification can steal focus), so check the activity before deciding.</p>`;
  },
};

register({
  'test.log': (el) => {
    const row = currentView().data.students.find((r) => r.student_id === Number(el.dataset.id));
    openModal({
      title: `${row.name}: activity`,
      body: html`<p class="sm">Started ${fmtDateTime(row.started_at)}${row.submitted_at ? `, finished ${fmtDateTime(row.submitted_at)}` : ''}.</p>
        ${row.log.length ? table(['When', 'What', 'Counted'], row.log.map((e) => [fmtDateTime(e.at), KIND[e.kind] || e.kind, e.counted ? 'Yes' : 'No (same switch)'])) : html`<p class="mu">Nothing recorded. The student stayed on the page.</p>`}`,
      foot: html`<button type="button" class="btn" data-act="modal.close">Close</button>`,
    });
  },
  'test.reset': async (el) => {
    if (!(await confirmBox({ title: `Let ${el.dataset.name} retake?`, text: 'Their current attempt is removed and they can start again while the test is open. This is recorded.', label: 'Allow retake' }))) return;
    const view = currentView();
    await api.post(`/trainer/tests/${view.data.test.id}/students/${el.dataset.id}/reset`);
    closeModal(false);
    toast('They can take the test again', 'check');
    await reload();
  },
});

export const routes = [newTest, editTest, results];
